import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import ChartsEmbedSDK from '@mongodb-js/charts-embed-dom';
import { isNil, toNumber } from 'lodash';
import clsx from 'clsx';

import FusePageSimple from '@fuse/core/FusePageSimple';

import Config from 'app/Config';
import { HttpService } from 'app/services';

import Chart from '../components/Chart';
import * as DashboardActions from '../store/actions';
import DashboardContext from './DashboardContext';
import MySchedule from '../components/Schedule';
import MyCalendar from '../components/Calendar';
import NoticeBoard from '../components/Notice';

const useStyles = makeStyles((theme) => ({
  content: {
    width: '100%',
    maxWidth: 'unset',
    height: 'calc(100vh - 60px)',
    maxHeight: 'calc(100vh - 60px)',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      height: 'auto',
      maxHeight: 'none',
      overflow: 'auto',
    },
  },
  contentWrapper: {
    padding: 0,
    width: '100%',
    boxSizing: 'border-box',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    '& > .w-full': {
      height: '100% !important',
      minHeight: '0 !important',
    },
    [theme.breakpoints.down('md')]: {
      height: 'auto',
      overflow: 'visible',
      '& > .w-full': {
        height: 'auto !important',
        minHeight: 'auto !important',
      },
    },
  },
  dashboardLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 30%)',
    gridTemplateRows: 'minmax(0, 1fr)',
    alignItems: 'stretch',
    width: '100%',
    height: '100%',
    minHeight: 0,
    boxSizing: 'border-box',
    gap: `${theme.spacing(1.5)}px`,
    padding: `${theme.spacing(2.25)}px ${theme.spacing(4)}px`,
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'auto',
      height: 'auto',
      overflow: 'visible',
      padding: '16px 24px',
      gap: `${theme.spacing(1.5)}px`,
    },
    [theme.breakpoints.down('sm')]: {
      padding: '12px 16px',
    },
  },
  leftPanel: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)',
    gridTemplateRows: 'minmax(0, 1fr) minmax(0, 1fr)',
    gridTemplateAreas: `
      "calendar schedule schedule"
      "charts charts charts"
    `,
    gap: `${theme.spacing(1)}px`,
    minWidth: 0,
    minHeight: 0,
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'auto',
      gridTemplateAreas: `
        "calendar"
        "schedule"
        "charts"
      `,
      height: 'auto',
    },
  },
  calendarCell: {
    gridArea: 'calendar',
    minWidth: 0,
    minHeight: 0,
    overflow: 'visible',
    [theme.breakpoints.down('sm')]: {
      minHeight: 360,
      overflow: 'visible',
    },
  },
  scheduleCell: {
    gridArea: 'schedule',
    minWidth: 0,
    minHeight: 0,
    overflow: 'visible',
    [theme.breakpoints.down('sm')]: {
      minHeight: 360,
      overflow: 'visible',
    },
  },
  chartsCell: {
    gridArea: 'charts',
    display: 'flex',
    minWidth: 0,
    minHeight: 0,
    overflow: 'visible',
    [theme.breakpoints.down('sm')]: {
      overflow: 'visible',
      minHeight: 280,
    },
  },
  chartsRow: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    gap: `${theme.spacing(1)}px`,
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
  },
  rightPanel: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
    height: '100%',
    overflow: 'visible',
    [theme.breakpoints.down('md')]: {
      height: 'auto',
      minHeight: 320,
      overflow: 'visible',
    },
  },
}));

const Dashboard = () => {
  const classes = useStyles();
  const {
    data: { currentPharmacyId, roleLevel },
  } = useSelector((state) => state.auth.user);
  const { data } = useSelector((state) => state.dashboard.home);
  const dispatch = useDispatch();
  const [sdk, setSDK] = useState(null);

  useEffect(() => {
    if (sdk) return;

    const chartsSdk = new ChartsEmbedSDK({
      baseUrl: Config.REACT_APP_CHART_URL,
      showAttribution: false,
      getUserToken: async () => {
        try {
          const { data: token } = await HttpService.get('/api/dashboardpermissions/charts-token');

          if (!token) {
            throw new Error('Charts token missing in response');
          }

          return token;
        } catch (error) {
          console.error('Failed to fetch charts token.', error);
          throw error;
        }
      },
    });

    setSDK(chartsSdk);
  }, [sdk]);

  useEffect(() => {
    if (isNil(roleLevel)) return;
    dispatch(DashboardActions.getDashboards(roleLevel));
  }, [roleLevel]);

  const sortedCharts = useMemo(
    () => [...(data || [])].sort((a, b) => (toNumber(a.order) > toNumber(b.order) ? 1 : -1)),
    [data],
  );

  if (isNil(currentPharmacyId) || isNil(sdk) || !data?.length) return null;
  return (
    <FusePageSimple
      contentWrapper={{ className: clsx(classes.content) }}
      classes={{
        content: clsx(classes.contentWrapper),
      }}
      content={
        <DashboardContext.Provider value={{ sdk }}>
          <div className={clsx(classes.dashboardLayout)}>
            <div className={clsx(classes.leftPanel)}>
              <div className={clsx(classes.calendarCell)}>
                <MyCalendar />
              </div>
              <div className={clsx(classes.scheduleCell)}>
                <MySchedule />
              </div>
              <div className={clsx(classes.chartsCell)}>
                <div className={clsx(classes.chartsRow)}>
                  {sortedCharts.map((chart) => (
                    <Chart key={`${chart.id}-${chart.order}`} {...chart} />
                  ))}
                </div>
              </div>
            </div>
            <div className={clsx(classes.rightPanel)}>
              <NoticeBoard />
            </div>
          </div>
        </DashboardContext.Provider>
      }
      innerScroll
    />
  );
};

export default Dashboard;
